import {
  Box,
  NavLink,
  rem,
  createStyles,
  Image,
  Text
} from "@mantine/core";
import {
  IconLogout,
  IconCalendar,
  IconHome,
  IconCalendarBolt,
  IconCalendarClock,
  IconGitPullRequest,
  IconRobot,
  IconPlug
} from "@tabler/icons-react";
import logo from "../../assets/Long_logo_light@4x.png";
import useLogout from "../../hooks/useLogout";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";

const useStyles = createStyles((theme) => ({
  control: {
    "&:hover": {
      border: `${rem(3)} solid ${
        theme.colorScheme === "dark"
          ? theme.colors.resirent[8]
          : theme.colors.resirent[8]
      }`,
      backgroundColor: theme.white,
      color: theme.colors.resirent[8]
    },
    border: `${rem(3)} solid ${
      theme.colorScheme === "dark"
        ? theme.colors.resirent[8]
        : theme.colors.resirent[8]
    }`,
    color: theme.white,
    width: "50%"
  },

  navbar: {
    minHeight: "100%",
    padding: "md",
    color: theme.white,
    backgroundColor: theme.fn.variant({ variant: "filled", color: "resirent" })
      .background
  },

  footer: {
    paddingTop: `calc(${theme.spacing.xl} * 3)`
  },

  link: {
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    fontSize: "var(--mantine-font-size-sm)",
    color: "var(--mantine-color-white)",
    padding: "var(--mantine-spacing-xs) var(--mantine-spacing-sm)",
    borderRadius: "var(--mantine-radius-sm)",
    fontWeight: 500,

    "&:hover": {
      backgroundColor: "var(--mantine-color-blue-7)",
      color: "var(--mantine-color-white)",

      "& .linkIcon": {
        color: "var(--mantine-color-white)"
      }
    },

    "[data-active]": {
      "&, &:hover": {
        boxShadow: "var(--mantine-shadow-sm)",
        backgroundColor: "var(--mantine-color-white)",
        color: "var(--mantine-color-blue-filled)",

        "& .linkIcon": {
          color: "var(--mantine-color-blue-filled)"
        }
      }
    }
  },

  linkIcon: {
    color: "var(--mantine-color-blue-1)",
    marginRight: "var(--mantine-spacing-sm)",
    width: "25px",
    height: "25px"
  },

  navlink: {
    "&:hover": {
      backgroundColor: theme.colors.resirent[8],
      color: theme.fn.variant({ variant: "filled", color: "resirent" })
        .background
    },

    "&:active": {
      variant: "filled",
      color: "resirentPink"
    }
  }
}));

const Navbar = () => {
  const { classes, theme } = useStyles();
  const { t } = useTranslation();
  const Logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();

  const data = [
    {
      link: "/dashboard/profile",
      label: t("dashboard_links.homepage"),
      icon: IconHome
    },
    {
      link: "/dashboard/calendar-settings",
      label: t("dashboard_links.calendar_settings"),
      icon: IconCalendar
    },
    {
      link: "/dashboard/calendar-view",
      label: t("dashboard_links.calendar_manager"),
      icon: IconCalendarBolt
    },
    {
      link: "/dashboard/calendar-cleaning",
      label: t("dashboard_links.calendar_cleaning"),
      icon: IconCalendarClock
    },
    {
      link: "/dashboard/property-requests",
      label: "Property Requests",
      icon: IconGitPullRequest
    },
    {
      link: "/dashboard/resibot",
      label: "ResiBot Manager",
      icon: IconRobot
    },
    {
      link: "/dashboard/channels",
      label: t("dashboard_links.channel_connections"),
      icon: IconPlug
    }
  ];

  const navActiveStyles = (theme) => ({
    root: {
      color: theme.white,
      '&:hover': {
        backgroundColor: theme.colors.resirent[8],
        color: theme.white,
      },
      '&[data-active]': {
        backgroundColor: theme.white,
        color: theme.colors.resirent[8],
      },
    },
    section: {
      color: 'inherit',
    },
  });

  const links = data.map((item) => (
    <NavLink
      className={classes.navlink}
      key={item.link}
      label={item.label}
      component={Link}
      to={item.link}
      active={location.pathname === item.link}
      styles={navActiveStyles}
      icon={
        <item.icon
          size={rem(20)}
          stroke={2}
          color="currentColor"
        />
      }
    />
  ));

  const signOut = async () => {
    await Logout();
    navigate(t("links.home"));
  };

  return (
    <Box className={classes.navbar}>
      <Box p="md">
        <a href={t("links.home")}>
          <Image src={logo} width={150} alt="ResiRent" />
        </a>
      </Box>

      <Box px="md" pb="xs" pt={0}>
        <Text size="sm" weight={600} color={theme.white}>
          {t("dashboard")}
        </Text>
      </Box>

      <Box px="md" pb="md">
        {links}
      </Box>

      <Box px="md" pb="md" pt="sm">
        <NavLink
          className={classes.navlink}
          label={t("logout.title")}
          component="button"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            signOut();
          }}
          styles={(th) => ({
            root: {
              color: th.white,
              width: '100%',
              '&:hover': {
                backgroundColor: theme.colors.resirent[8],
                color: th.white,
              },
            },
          })}
          icon={
            <IconLogout size={rem(20)} stroke={2} color="currentColor" />
          }
        />
      </Box>
    </Box>
  );
};

export default Navbar;
